import fs from "node:fs/promises";
import path from "node:path";
import Ajv from "ajv";
import addFormats from "ajv-formats";
import { hashFile } from "./workflow-artifacts.js";
import { getPackageRoot } from "../package-assets.js";

export interface ApprovalReceipt {
  workflowId: string;
  approvedSpecHash: string;
  approvedBy: string;
  approvedAt: string;
  authorizationSource: string;
}

export interface ApprovalValidationResult {
  valid: boolean;
  reason?: string;
  receipt?: ApprovalReceipt;
  approvedSpecHash?: string;
}

export class ApprovalReceiptValidator {
  cwd: string;

  constructor({ cwd = process.cwd() }: { cwd?: string } = {}) {
    this.cwd = path.resolve(cwd);
  }

  async validate(receiptPath: string, specPath: string, expectedWorkflowId?: string): Promise<ApprovalValidationResult> {
    const absoluteReceiptPath = path.resolve(this.cwd, receiptPath);
    if (absoluteReceiptPath !== this.cwd && !absoluteReceiptPath.startsWith(`${this.cwd}${path.sep}`)) {
      return { valid: false, reason: "Approval receipt must be inside the repository." };
    }

    try {
      const stat = await fs.lstat(absoluteReceiptPath);
      if (!stat.isFile() || stat.isSymbolicLink()) {
        return { valid: false, reason: "Approval receipt must be a regular, non-symlink file." };
      }
    } catch {
      return { valid: false, reason: `Approval receipt not found: ${receiptPath}` };
    }

    let receipt: ApprovalReceipt;
    try {
      receipt = JSON.parse(await fs.readFile(absoluteReceiptPath, "utf8"));
      const root = getPackageRoot();
      const schema = JSON.parse(await fs.readFile(path.join(root, "dist-assets/schemas/approval-receipt.schema.json"), "utf8"));
      const ajv = new Ajv({ allErrors: true });
      addFormats(ajv);
      const validateSchema = ajv.compile(schema);
      if (!validateSchema(receipt)) {
        const errors = (validateSchema.errors || []).map((error) => `${error.instancePath} ${error.message}`).join(", ");
        return { valid: false, reason: `Approval receipt schema violation: ${errors}` };
      }
    } catch (error: any) {
      return { valid: false, reason: `Approval receipt validation failed: ${error.message}` };
    }

    if (expectedWorkflowId && receipt.workflowId !== expectedWorkflowId) {
      return { valid: false, reason: `Approval receipt workflowId '${receipt.workflowId}' does not match '${expectedWorkflowId}'.` };
    }

    const approvedSpecHash = await hashFile(specPath);
    if (receipt.approvedSpecHash !== approvedSpecHash) {
      return { valid: false, reason: "Approval receipt hash does not match the approved specification bytes." };
    }

    return { valid: true, receipt, approvedSpecHash };
  }
}
